class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cache = new Map();
    this.cacheTtl = 60 * 1000;
    this.suggestionCache = new Map();
    this.suggestionCacheTtl = 60 * 1000;
    // Avoid flashing a spinner for the common cold-cache response (~1.4s
    // including input debounce). Keep it as a fallback for genuinely slow
    // requests rather than showing it for only the final few milliseconds.
    this.loadingDelayMs = 1500;
    this.loadingTimer = null;
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isModalSearch = !!this.closest('.search-modal');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.requestSequence = 0;
    this.searchTerm = '';
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    const searchModal = document.querySelector('.search-modal .modal__content');
    if (searchModal) {
      searchModal.addEventListener('classChange', this.onModalClassChange.bind(this));
    }

    document.addEventListener('locationChanged', () => {
      this.cancelActiveRequest();
      if (this.isOpen && this.searchTerm) {
        this.getSearchResults(this.searchTerm);
      }
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  getCurrentLocation() {
    return getActiveSearchLocation();
  }

  hasExplicitStore() {
    return Boolean(this.getCurrentLocation());
  }

  // Cache and request scope is browse until a real store is selected.
  getEffectiveLocation() {
    return this.hasExplicitStore() ? this.getCurrentLocation() : 'browse';
  }

  normalizeTerm(term) {
    return String(term || '').trim().replace(/\s+/g, ' ').toLowerCase();
  }

  getCacheKey(term, location) {
    return `${this.normalizeTerm(term)}::location-${location}`;
  }

  getCachedResult(key) {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.createdAt >= this.cacheTtl) {
      this.cache.delete(key);
      return null;
    }
    return entry.data;
  }

  setCachedResult(key, data) {
    this.cache.set(key, { createdAt: Date.now(), data });
    const now = Date.now();
    for (const [cachedKey, entry] of this.cache) {
      if (now - entry.createdAt >= this.cacheTtl) this.cache.delete(cachedKey);
    }
  }

  getSuggestionCacheKey(term) {
    return this.normalizeTerm(term);
  }

  getCachedSuggestion(key) {
    const entry = this.suggestionCache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.createdAt >= this.suggestionCacheTtl) {
      this.suggestionCache.delete(key);
      return null;
    }
    return entry.data;
  }

  setCachedSuggestion(key, data) {
    this.suggestionCache.set(key, { createdAt: Date.now(), data });
    const now = Date.now();
    for (const [cachedKey, entry] of this.suggestionCache) {
      if (now - entry.createdAt >= this.suggestionCacheTtl) this.suggestionCache.delete(cachedKey);
    }
  }

  clearLoadingTimer() {
    if (this.loadingTimer) {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
    }
  }

  cancelActiveRequest() {
    this.clearLoadingTimer();
    this.requestSequence += 1;
    this.abortController.abort();
    this.abortController = new AbortController();
  }

  startRequest(term, location) {
    this.cancelActiveRequest();
    return {
      sequence: this.requestSequence,
      term,
      location,
      signal: this.abortController.signal,
    };
  }

  isCurrentRequest(request) {
    return (
      request.sequence === this.requestSequence &&
      request.term === this.searchTerm &&
      request.location === this.getEffectiveLocation() &&
      !request.signal.aborted
    );
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();

    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) {
      this.cancelActiveRequest();
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a, a[aria-selected="true"]')
    ) {
      event.preventDefault();
      return;
    }
    this.close();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.cancelActiveRequest();
      this.closeResults(true);
    }
  }

  searchModalOwnsSearchUI() {
    return (
      !this.isModalSearch &&
      document.querySelector('.search-modal.modal__content') !== null &&
      !document.body.classList.contains('template-cart')
    );
  }

  searchModalIsOpen() {
    const searchModal = document.querySelector('.search-modal.modal__content');
    return !!searchModal && searchModal.classList.contains('open_modal');
  }

  onFocus() {
    if (this.searchModalOwnsSearchUI()) return;

    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll(
        'li, button.predictive-search__item, a.predictive-search__item--term'
      )
    ).filter((element) => element.offsetParent !== null);
    if (!allVisibleElements.length || (moveUp && !selectedElement)) return;

    const selectedElementIndex = allVisibleElements.indexOf(selectedElement);
    let activeElementIndex = 0;
    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement || activeElement === selectedElement) return;
    activeElement.setAttribute('aria-selected', 'true');
    selectedElement?.setAttribute('aria-selected', 'false');
    this.input.setAttribute('aria-activedescendant', activeElement.id || '');
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, a[aria-selected="true"], button[aria-selected="true"]'
    );
    selectedOption?.click();
  }

  getBrowseLocationPriceKeys() {
    const matrix = typeof storeMatrix !== 'undefined' ? storeMatrix : [];
    const locations = new Set(Object.keys(matrix[0] || window.locationManager?.locations || {}));
    document.querySelectorAll('[data-location-prices]').forEach((element) => {
      try {
        Object.keys(JSON.parse(element.getAttribute('data-location-prices')) || {}).forEach((key) => locations.add(key));
      } catch (e) {
        // Keep the canonical stores when one card has malformed data.
      }
    });
    return Array.from(locations).filter((key) => /^\d+$/.test(key)).map((key) => `price_location_${key}`);
  }

  getBrowsePrice(product) {
    const prices = (product.locationPrices || []).filter((field) =>
      field && field.key !== 'price_location_1' && /^price_location_\d+$/.test(field.key) && /^\d+(?:\.\d+)?$/.test(String(field.value).trim())
    ).map((field) => Number(field.value)).filter((price) => Number.isFinite(price) && price > 0);
    return prices.length ? Math.max(...prices) : null;
  }

  getStorefrontProductsQuery() {
    return `
      query StorefrontPredictiveProducts(
        $query: String!
        $filters: [ProductFilter!]
        $priceKey: String!
        $unitPriceKey: String!
        $specialTextKey: String!
        $additionalKey: String!
      ) {
        search(
          query: $query
          first: 30
          types: [PRODUCT]
          prefix: LAST
          sortKey: RELEVANCE
          productFilters: $filters
          unavailableProducts: SHOW
        ) {
          nodes {
            ... on Product {
              id
              title
              handle
              onlineStoreUrl
              productType
              tags
              featuredImage {
                url(transform: { maxWidth: 360 })
                altText
                width
                height
              }
              availableLocationsMetafield: metafield(namespace: "custom", key: "available_locations") {
                value
              }
              specialLocations: metafield(namespace: "custom", key: "special_locations") {
                value
              }
              locationPrices: metafields(identifiers: [${this.getBrowseLocationPriceKeys()
                .map((key) => `{namespace: "custom", key: "${key}"}`).join(',')}]) {
                key
                value
              }
              locationPrice: metafield(namespace: "custom", key: $priceKey) {
                value
              }
              locationUnitPrice: metafield(namespace: "custom", key: $unitPriceKey) {
                value
              }
              locationSpecialText: metafield(namespace: "custom", key: $specialTextKey) {
                value
              }
              locationAdditional: metafield(namespace: "custom", key: $additionalKey) {
                value
              }
              uom: metafield(namespace: "local", key: "uom") {
                value
              }
              conversion: metafield(namespace: "local", key: "conversion") {
                value
              }
              overrideCup: metafield(namespace: "local", key: "overridecup") {
                value
              }
              hideInState: metafield(namespace: "local", key: "hideInState") {
                value
              }
              variants(first: 1, sortKey: POSITION) {
                nodes {
                  id
                  sku
                  price {
                    amount
                  }
                }
              }
            }
          }
        }
      }
    `;
  }

  getStorefrontSuggestionsQuery() {
    return `
      query StorefrontPredictiveSuggestions($query: String!) {
        predictiveSearch(
          query: $query
          limit: 4
          limitScope: EACH
          types: [QUERY, COLLECTION]
          unavailableProducts: SHOW
        ) {
          queries {
            text
          }
          collections {
            id
            title
            handle
          }
        }
      }
    `;
  }

  getStorefrontVariables(term, location) {
    const trimmedTerm = String(term || '').trim();
    const productQuery = trimmedTerm.endsWith('*') ? trimmedTerm : `${trimmedTerm}*`;

    if (location === 'browse' || !this.hasExplicitStore()) {
      // Browse the union catalogue. The full locationPrices field supplies the
      // display maximum; selected-store fields are intentionally unused.
      return {
        query: productQuery,
        filters: [],
        priceKey: 'price_location_none',
        unitPriceKey: 'unit_price_location_none',
        specialTextKey: 'special_text_location_none',
        additionalKey: 'additional_location_none',
      };
    }

    return {
      query: productQuery,
      filters: [
        {
          productMetafield: {
            namespace: 'custom',
            key: 'available_locations',
            value: location,
          },
        },
      ],
      priceKey: `price_location_${location}`,
      unitPriceKey: `unit_price_location_${location}`,
      specialTextKey: `special_text_location_${location}`,
      additionalKey: `additional_location_${location}`,
    };
  }

  canUseStorefrontSearch() {
    const manager = window.locationManager;
    return !!(
      manager &&
      manager.storefrontToken &&
      manager.shopDomain &&
      manager.apiVersion
    );
  }

  async executeStorefrontProductsSearch(term, location, signal) {
    const manager = window.locationManager;
    if (!this.canUseStorefrontSearch()) {
      throw new Error('Storefront search transport is unavailable');
    }

    const endpoint = `https://${manager.shopDomain}/api/${manager.apiVersion}/graphql.json`;
    const response = await fetch(endpoint, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': manager.storefrontToken,
      },
      body: JSON.stringify({
        query: this.getStorefrontProductsQuery(),
        variables: this.getStorefrontVariables(term, location),
      }),
    });

    if (!response.ok) {
      throw new Error(`Storefront product search failed (${response.status})`);
    }

    const payload = await response.json();
    if (payload.errors?.length) {
      throw new Error(payload.errors.map((error) => error.message).join(', '));
    }
    if (!payload.data?.search) {
      throw new Error('Storefront product search returned incomplete data');
    }
    return payload.data.search;
  }

  async executeStorefrontSuggestionsSearch(term, signal) {
    const manager = window.locationManager;
    if (!this.canUseStorefrontSearch()) {
      throw new Error('Storefront search transport is unavailable');
    }

    const endpoint = `https://${manager.shopDomain}/api/${manager.apiVersion}/graphql.json`;
    const response = await fetch(endpoint, {
      method: 'POST',
      signal,
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': manager.storefrontToken,
      },
      body: JSON.stringify({
        query: this.getStorefrontSuggestionsQuery(),
        variables: { query: term },
      }),
    });

    if (!response.ok) {
      throw new Error(`Storefront suggestions request failed (${response.status})`);
    }

    const payload = await response.json();
    if (payload.errors?.length) {
      throw new Error(payload.errors.map((error) => error.message).join(', '));
    }
    if (!payload.data?.predictiveSearch) {
      throw new Error('Storefront suggestions response was incomplete');
    }
    return payload.data.predictiveSearch;
  }

  async fetchCart(signal) {
    const response = await fetch('/cart.js', {
      signal,
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
    return response.json();
  }

  async getSearchResults(searchTerm) {
    const term = String(searchTerm || '').trim();
    const hasStore = this.hasExplicitStore();
    // 'browse' is the cache and request scope for no selected store.
    const location = hasStore ? this.getCurrentLocation() : 'browse';
    const request = this.startRequest(term, location);
    const cacheKey = this.getCacheKey(term, location);
    this.setLiveRegionLoadingState(request);
    const cartPromise = this.fetchCart(request.signal).catch((error) => {
      if (error.name === 'AbortError') throw error;
      console.warn('[PredictiveSearch] Cart quantity request failed:', error);
      return null;
    });

    try {
      if (!this.canUseStorefrontSearch()) {
        await this.getNativeFallbackResults(request, cartPromise);
        return;
      }

      let searchData = this.getCachedResult(cacheKey);

      if (!searchData) {
        searchData = await this.executeStorefrontProductsSearch(term, location, request.signal);
        if (!this.isCurrentRequest(request)) return;
        this.setCachedResult(cacheKey, searchData);
      } else if (!this.isCurrentRequest(request)) {
        return;
      }

      this.renderStorefrontResults(searchData, request);
      this.hydrateCartStateWhenReady(cartPromise, request);
      this.loadSuggestions(term, location, request);
    } catch (error) {
      if (error.name === 'AbortError' || !this.isCurrentRequest(request)) return;
      console.error('[PredictiveSearch] Storefront product search failed; using native fallback:', error);
      await this.getNativeFallbackResults(request, cartPromise);
    }
  }

  loadSuggestions(term, location, request) {
    const suggestionKey = this.getSuggestionCacheKey(term);
    const cachedSuggestions = this.getCachedSuggestion(suggestionKey);
    if (cachedSuggestions) {
      this.hydrateSuggestions(cachedSuggestions, term, location, request);
      return;
    }

    this.executeStorefrontSuggestionsSearch(term, request.signal)
      .then((suggestions) => {
        if (!this.isCurrentRequest(request)) return;
        this.setCachedSuggestion(suggestionKey, suggestions);
        this.hydrateSuggestions(suggestions, term, location, request);
      })
      .catch((error) => {
        if (error.name === 'AbortError' || !this.isCurrentRequest(request)) return;
        // A failed suggestions request quietly keeps the fallback "Search for"
        // link already rendered alongside the products; it must never trigger
        // native product fallback or otherwise disturb rendered products.
        console.warn('[PredictiveSearch] Suggestions request failed; keeping fallback search link:', error);
      });
  }

  hydrateSuggestions(suggestions, term, location, request) {
    if (!this.isCurrentRequest(request)) return;
    const suggestionsGroup = this.predictiveSearchResults.querySelector(
      '#predictive-search-results-groups-wrapper .predictive-search__result-group'
    );
    if (!suggestionsGroup) return;

    suggestionsGroup.innerHTML = `
      ${this.renderSuggestionList(suggestions?.queries || [], suggestions?.collections || [], term, location)}
      ${this.renderPagesList(suggestions?.pages || [], suggestions?.articles || [], 'desktop')}
    `;
    updatePredictiveSearchLocation();

    const queries = suggestions?.queries || [];
    const collections = suggestions?.collections || [];
    const productCount = this.predictiveSearchResults.querySelectorAll(
      '#predictive-search-results-products-list .product-card'
    ).length;
    const totalCount = productCount + queries.length + collections.length;
    const countElement = this.predictiveSearchResults.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );
    if (countElement) {
      countElement.textContent = totalCount === 1 ? '1 result' : `${totalCount} results`;
    }
  }

  hydrateCartStateWhenReady(cartPromise, request) {
    cartPromise
      .then((cart) => {
        if (!cart || !this.isCurrentRequest(request)) return;
        this.seedCartState(this.predictiveSearchResults, cart);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.warn('[PredictiveSearch] Cart state hydration failed:', error);
        }
      });
  }

  parseAvailableLocations(value) {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.map(String);
    } catch (error) {
      // Some historical products contain comma-separated values.
    }
    return String(value)
      .split(',')
      .map((item) => item.trim().replace(/^["']|["']$/g, ''))
      .filter(Boolean);
  }

  isHiddenAtLocation(value, location) {
    if (!value || value === '-') return false;
    return String(value)
      .split(',')
      .map((item) => item.trim())
      .includes(String(location));
  }

  getCanonicalVariant(product) {
    // Variant 1-only catalogue: every product has exactly one live variant,
    // so the first (and only) node is always the canonical one.
    return product?.variants?.nodes?.[0] || null;
  }

  normalizeProduct(product, location) {
    if (!product?.id || !product?.handle || !product?.title) return null;
    // Storefront API channel visibility does not prove Online Store publication.
    if (typeof product.onlineStoreUrl !== 'string' || !product.onlineStoreUrl.trim()) return null;
    const tags = Array.isArray(product.tags) ? product.tags : [];
    const lowerTags = tags.map((tag) => String(tag).toLowerCase());
    const title = String(product.title);
    const titleLower = title.toLowerCase();
    const isDeliveryPass = titleLower.includes('delivery pass');
    const isGiftCard = titleLower.includes('gift card');
    const isDigital = isDeliveryPass || isGiftCard;
    const variant = isDigital
      ? product.variants?.nodes?.[0] || null
      : this.getCanonicalVariant(product);
    if (!variant?.id) return null;
    if (!/^\d+$/.test(this.numericId(variant.id))) return null;
    if (lowerTags.includes('frozen')) return null;

    const availableLocations = this.parseAvailableLocations(
      product.availableLocationsMetafield?.value
    );
    const maxQuantityTag = tags.find((tag) =>
      String(tag).toLowerCase().startsWith('max-quantity_')
    );
    const maxQuantityValue = Number.parseInt(
      String(maxQuantityTag || '').split('_').pop(),
      10
    );
    const maxQuantity =
      Number.isFinite(maxQuantityValue) && maxQuantityValue > 0
        ? maxQuantityValue
        : isDeliveryPass
          ? 1
          : 20;

    const isBrowseMode = location === 'browse' || !this.hasExplicitStore();

    if (isBrowseMode) {
      // Browse the union catalogue with the highest positive ERP price from
      // every location except 1, independently of ranging or selected-store specials.
      const hasAvailableLocationsValue = !!product.availableLocationsMetafield?.value;
      if (hasAvailableLocationsValue && availableLocations.length === 0) return null;

      const displayPrice = isDigital
        ? Number.parseFloat(variant.price?.amount)
        : this.getBrowsePrice(product);
      if (!(displayPrice > 0)) return null;

      return {
        ...product,
        variant,
        tags,
        lowerTags,
        availableLocations,
        locationPrice: displayPrice,
        isDeliveryPass,
        isGiftCard,
        isDigital,
        isLiquor: lowerTags.includes('liquor'),
        isFrozen: lowerTags.includes('frozen'),
        selectedLocation: '',
        maxQuantity,
      };
    }

    const locationPrice = Number.parseFloat(product.locationPrice?.value);

    if (!isDigital) {
      if (!availableLocations.includes(String(location))) return null;
      if (!(locationPrice > 0)) return null;
    }
    if (isGiftCard && !(locationPrice > 0)) return null;
    if (this.isHiddenAtLocation(product.hideInState?.value, location)) return null;

    let displayPrice = locationPrice;
    if (isDeliveryPass && !(displayPrice > 0)) {
      displayPrice = Number.parseFloat(variant.price?.amount);
    }
    if (!(displayPrice > 0)) return null;

    return {
      ...product,
      variant,
      tags,
      lowerTags,
      availableLocations,
      locationPrice: displayPrice,
      isDeliveryPass,
      isGiftCard,
      isDigital,
      isLiquor: lowerTags.includes('liquor'),
      isFrozen: lowerTags.includes('frozen'),
      selectedLocation: String(location),
      maxQuantity,
    };
  }

  getCartQuantities(cart) {
    const quantities = new Map();
    for (const item of cart?.items || []) {
      const id = String(item.variant_id || item.id || '');
      if (!id) continue;
      quantities.set(id, (quantities.get(id) || 0) + Number(item.quantity || 0));
    }
    return quantities;
  }

  isEachUom(uom) {
    const normalized = String(uom || '')
      .toLowerCase()
      .replace(/\$/g, '')
      .replace(/\d+(?:\.\d+)?/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    return (
      normalized === 'each' ||
      normalized === 'ea' ||
      normalized.includes('/ each') ||
      normalized.includes('per each') ||
      /(^|[\s/])each\b/.test(normalized) ||
      /(^|\s)ea\b/.test(normalized)
    );
  }

  isProduceProduct(product) {
    const type = String(product.productType || '').toLowerCase();
    const tags = product.lowerTags.join(' ');
    return (
      type.includes('fruit') ||
      type.includes('veg') ||
      tags.includes('fruit') ||
      tags.includes('vegetable')
    );
  }

  getPriceDisplay(product) {
    const price = !this.hasExplicitStore() && !product.isDigital
      ? this.getBrowsePrice(product)
      : product.locationPrice;
    const rawUom = String(product.uom?.value || '');
    // Selling unit beside main price is always ea; cup UOM keeps the ERP value (e.g. "/ litre").
    const uom = 'ea';
    const hasStore = this.hasExplicitStore();

    let isSpecial = false;
    let additionalDisplay = '';
    if (hasStore) {
      // custom.special_locations is the sole source of truth (not legacy special_location_XX booleans).
      const currentLocation = String(this.getCurrentLocation() || product.selectedLocation || '');
      const specialLocationsRaw = product.specialLocations?.value;
      if (specialLocationsRaw) {
        try {
          const specialLocations = JSON.parse(specialLocationsRaw);
          isSpecial = Array.isArray(specialLocations) &&
            specialLocations.map(String).includes(currentLocation);
        } catch (e) {
          isSpecial = false;
        }
      }
      const specialText = this.isUsefulText(product.locationSpecialText?.value)
        ? product.locationSpecialText.value
        : '';
      const additional = this.isUsefulText(product.locationAdditional?.value)
        ? product.locationAdditional.value
        : '';
      // Save/additional copy only when this store is actually on special.
      additionalDisplay = isSpecial ? (specialText || additional) : '';
    }
    // Browse mode (Option B): never a special/save badge before a store is
    // chosen - custom.special_locations is a store-scoped metafield.

    let unitPriceHtml = '';
    if (rawUom && !(this.isEachUom(rawUom) && this.isProduceProduct(product))) {
      const conversion = Number.parseFloat(product.conversion?.value);
      const overrideCup = String(product.overrideCup?.value || '').toLowerCase() === 'yes';
      let unitValue = 0;
      if (hasStore) {
        const unitPrice = Number.parseFloat(product.locationUnitPrice?.value);
        unitValue = unitPrice;
        if (!(unitValue > 0) || overrideCup) {
          unitValue = conversion > 0 ? price * conversion : 0;
        }
      } else {
        // Browse mode: locationUnitPrice is a store metafield - never surface
        // it before a store is chosen; convert from the displayed maximum.
        unitValue = conversion > 0 ? price * conversion : 0;
      }
      if (unitValue > 0) {
        unitPriceHtml =
          `<span class="unit_price_display"><small>$${unitValue.toFixed(2)} ` +
          `${this.escapeHtml(rawUom)}</small></span>`;
      }
    }

    return {
      formattedPrice: price > 0 ? `$${price.toFixed(2)}` : '',
      uom,
      isSpecial,
      additionalDisplay,
      unitPriceHtml,
    };
  }

  isTruthyMetafield(value) {
    return ['true', '1', 'yes'].includes(String(value || '').trim().toLowerCase());
  }

  isUsefulText(value) {
    const normalized = String(value || '').trim().toLowerCase();
    return !!normalized && normalized !== '-' && normalized !== 'undefined';
  }

  renderStorefrontResults(searchData, request) {
    if (!this.isCurrentRequest(request)) return;
    const products = (searchData.nodes || [])
      .map((product) => this.normalizeProduct(product, request.location))
      .filter(Boolean)
      .slice(0, 10);
    // Suggestions are fetched separately (see loadSuggestions) so products never
    // wait on predictiveSearch latency; the sidebar renders its own stable
    // "Search for …" fallback until suggestions hydrate in place.
    const markup = this.buildResultsMarkup(
      products,
      null,
      new Map(),
      request.term,
      request.location
    );
    this.renderSearchResults(markup);
  }

  buildResultsMarkup(products, suggestions, cartQuantities, term, location) {
    const queries = suggestions?.queries || [];
    const collections = suggestions?.collections || [];
    const pages = suggestions?.pages || [];
    const articles = suggestions?.articles || [];
    const suggestionCount =
      queries.length + collections.length + pages.length + articles.length;
    const totalCount = products.length + suggestionCount;
    const wrapperClass = products.length
      ? 'predictive-search__results-groups-wrapper'
      : 'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-products';

    return `
      <div id="predictive-search-results" role="listbox">
        <div id="predictive-search-results-groups-wrapper" class="${wrapperClass}">
          <div class="predictive-search__result-group">
            ${this.renderSuggestionList(queries, collections, term, location)}
            ${this.renderPagesList(pages, articles, 'desktop')}
          </div>
          <div class="predictive-search__result-group">
            ${this.renderProductsList(products, cartQuantities, term, location)}
            ${this.renderPagesList(pages, articles, 'mobile')}
          </div>
        </div>
        ${this.renderLoadingState()}
      </div>
      <span class="hidden" data-predictive-search-live-region-count-value>
        ${totalCount === 1 ? '1 result' : `${totalCount} results`}
      </span>
    `;
  }

  renderSuggestionList(queries, collections, term, location) {
    const queryItems = queries.map((query, index) => {
      const text = String(query.text || '').trim();
      if (!text) return '';
      const url = this.buildSearchUrl(text, location);
      return this.renderSuggestionItem(
        `predictive-search-option-query-${index + 1}`,
        text,
        url,
        'predictive-search__item-query-result h5'
      );
    }).join('');

    const collectionItems = collections.map((collection, index) => {
      const title = String(collection.title || '').trim();
      const handle = String(collection.handle || '').trim();
      if (!title || !handle) return '';
      return this.renderSuggestionItem(
        `predictive-search-option-collection-${index + 1}`,
        title,
        `/collections/${encodeURIComponent(handle)}`
      );
    }).join('');

    const items = queryItems + collectionItems ||
      this.renderSuggestionItem(
        'predictive-search-option-query-empty',
        `Search for “${term}”`,
        this.buildSearchUrl(term, location)
      );

    return `
      <div>
        <h2 id="predictive-search-queries" class="predictive-search__heading text-body caption-with-letter-spacing">
          Suggestions
        </h2>
        <ul id="predictive-search-results-queries-list"
            class="predictive-search__results-list list-unstyled"
            role="group"
            aria-labelledby="predictive-search-queries">
          ${items}
        </ul>
      </div>
    `;
  }

  renderSuggestionItem(id, title, url, extraHeadingClass = 'h5') {
    return `
      <li id="${this.escapeAttr(id)}"
          class="predictive-search__list-item"
          role="option"
          aria-selected="false">
        <a href="${this.escapeAttr(url)}"
           class="predictive-search__item link link--text"
           tabindex="-1">
          <div class="predictive-search__item-content predictive-search__item-content--centered">
            <p class="predictive-search__item-heading ${this.escapeAttr(extraHeadingClass)}"
               aria-label="${this.escapeAttr(title)}">${this.escapeHtml(title)}</p>
          </div>
        </a>
      </li>
    `;
  }

  renderPagesList(pages, articles, viewport) {
    if (!pages.length && !articles.length) return '';
    const suffix = viewport === 'mobile' ? 'mobile' : 'desktop';
    const pageItems = pages.map((page, index) => {
      const title = String(page.title || '').trim();
      const handle = String(page.handle || '').trim();
      if (!title || !handle) return '';
      return this.renderSuggestionItem(
        `predictive-search-option-page-${suffix}-${index + 1}`,
        title,
        `/pages/${encodeURIComponent(handle)}`
      );
    }).join('');
    const articleItems = articles.map((article, index) => {
      const title = String(article.title || '').trim();
      const articleHandle = String(article.handle || '').trim();
      const blogHandle = String(article.blog?.handle || '').trim();
      if (!title || !articleHandle || !blogHandle) return '';
      return this.renderSuggestionItem(
        `predictive-search-option-article-${suffix}-${index + 1}`,
        title,
        `/blogs/${encodeURIComponent(blogHandle)}/${encodeURIComponent(articleHandle)}`
      );
    }).join('');

    return `
      <div class="predictive-search__pages-wrapper">
        <h2 id="predictive-search-pages-${suffix}"
            class="predictive-search__heading text-body caption-with-letter-spacing">Pages</h2>
        <ul id="predictive-search-results-pages-list-${suffix}"
            class="predictive-search__results-list list-unstyled"
            role="group"
            aria-labelledby="predictive-search-pages-${suffix}">
          ${pageItems}${articleItems}
        </ul>
      </div>
    `;
  }

  renderProductsList(products, cartQuantities, term, location) {
    // "Products" heading and the "Search for …" link share one compact row
    // (instead of a separate full-width button after all product cards) so
    // shoppers can reach full results without scrolling past the grid.
    const headingRow = `
      <div class="predictive-search__products-heading-row">
        <h2 id="predictive-search-products"
            class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2>
        ${this.renderSearchForLink(term, location)}
      </div>
    `;

    if (!products.length) {
      const emptyMessage = (location === 'browse' || !this.hasExplicitStore())
        ? 'No matching products.'
        : 'No products available at this location.';
      return `
        <div>
          ${headingRow}
          <p class="predictive-search__no-products">${emptyMessage}</p>
        </div>
      `;
    }

    return `
      <div>
        ${headingRow}
        <ul id="predictive-search-results-products-list"
            class="predictive-search__results-list list-unstyled"
            role="group"
            aria-labelledby="predictive-search-products">
          ${products.map((product, index) =>
            this.renderProductCard(
              product,
              cartQuantities.get(this.numericId(product.variant.id)) || 0,
              index
            )
          ).join('')}
        </ul>
      </div>
    `;
  }

  renderProductCard(product, cartQuantity, index) {
    const productId = this.numericId(product.id);
    const variantId = this.numericId(product.variant.id);
    const productUrl = `/products/${encodeURIComponent(product.handle)}`;
    const price = this.getPriceDisplay(product);
    const inCart = cartQuantity > 0;
    const canQuickAdd = !product.isDigital;
    const wrapperId = `predictive-add-${productId}-${index}`;
    const maxQuantity = product.maxQuantity;
    const badges = product.tags
      .filter((tag) => String(tag).toLowerCase().startsWith('badge_'))
      .map((tag) => `<li class="badge">${this.escapeHtml(String(tag).slice(6))}</li>`)
      .join('');

    return `
      <li id="predictive-search-option-product-${index + 1}"
          class="product-card product-item card-in-stock${inCart ? ' in-cart' : ''}"
          role="option"
          aria-selected="false"
          data-graphql-card="true"
          data-processed="true"
          data-product-handle="${this.escapeAttr(product.handle)}"
          data-title="${this.escapeAttr(product.title)}"
          data-variant-id="${this.escapeAttr(variantId)}"
          data-shopify-sku="${this.escapeAttr(product.variant.sku || '')}"
          data-uom="${this.escapeAttr(product.uom?.value || '')}"
          data-conversion-factor="${this.escapeAttr(product.conversion?.value || '')}"
          data-overridecupvalue="${this.escapeAttr(product.overrideCup?.value || '')}"
          data-hideinstate="${this.escapeAttr(product.hideInState?.value || '')}"
          data-available-locations="${this.escapeAttr(JSON.stringify(product.availableLocations))}"
          data-location-prices="${this.escapeAttr(JSON.stringify(
            Object.fromEntries((product.locationPrices || [])
              .filter((field) => field && /^price_location_\d+$/.test(field.key))
              .map((field) => [field.key.slice('price_location_'.length), field.value]))
          ))}"
          data-frozen="${product.isFrozen ? 'true' : 'false'}"
          data-alcohol="${product.isLiquor ? 'yes' : 'no'}"
          data-max-quantity="${maxQuantity}"
          ${product.isDigital ? 'data-gift-card-product' : ''}>
        <div class="card-wrapper product-card-wrapper product-card-wrapper-${this.escapeAttr(productId)}">
          ${badges ? `<ul class="badge_list">${badges}</ul>` : ''}
          <div class="badge-stack">
            <div class="special-badge"${price.isSpecial ? ' style="display: block;"' : ''}>Special</div>
          </div>
          <div class="card card--standard card--media" style="--ratio-percent: 100%;">
            <a href="${this.escapeAttr(productUrl)}" class="full-link" tabindex="-1"></a>
            <div class="card__inner ratio" style="--ratio-percent: 100%;">
              ${this.renderProductImage(product, index)}
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading">
                    <a href="${this.escapeAttr(productUrl)}"
                       id="StandardCardNoMediaLink-predictive-${this.escapeAttr(productId)}"
                       class="full-unstyled-link"
                       tabindex="-1">${this.escapeHtml(product.title)}</a>
                  </h3>
                </div>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h3" id="title-predictive-${this.escapeAttr(productId)}">
                  <a href="${this.escapeAttr(productUrl)}"
                     id="CardLink-predictive-${this.escapeAttr(productId)}"
                     class="full-unstyled-link"
                     tabindex="-1">${this.escapeHtml(product.title)}</a>
                </h3>
                ${product.isGiftCard
                  ? '<div class="gift-card-disclaimer">Not available on click &amp; collect - view product for details.</div>'
                  : ''}
                <div class="card-information">
                  <div class="caption-with-letter-spacing light" style="color: #fff;font-size: 0;">
                    ${this.escapeHtml(product.variant.sku || '')}
                  </div>
                  <div class="dynamic-price__wrapper">
                    <div class="additional${price.additionalDisplay ? ' show-additional' : ''}">
                      ${this.escapeHtml(price.additionalDisplay)}
                    </div>
                    <span class="sale">
                      <span class="unit_price${price.isSpecial ? ' is-special price-special-color' : ''}">
                        <strong><span class="from_price">${price.formattedPrice}</span>
                          <small>${this.escapeHtml(price.uom)}</small></strong>
                      </span>
                      ${price.unitPriceHtml}
                    </span>
                  </div>
                  <div class="product-error__container"></div>
                </div>
              </div>
              ${canQuickAdd
                ? this.renderQuickAdd(product, wrapperId, variantId, maxQuantity, cartQuantity)
                : this.renderSafeViewButton(productUrl)}
            </div>
          </div>
        </div>
      </li>
    `;
  }

  renderProductImage(product, index) {
    const image = product.featuredImage;
    const imageUrl = this.safeImageUrl(image?.url);
    if (!imageUrl) return '';
    return `
      <div class="card__media">
        <div class="media media--transparent media--hover-effect">
          <img src="${this.escapeAttr(imageUrl)}"
               alt="${this.escapeAttr(image.altText || product.title)}"
               class="motion-reduce"
               ${index > 1 ? 'loading="lazy"' : ''}
               ${index === 0 ? 'fetchpriority="high"' : ''}
               decoding="async"
               width="${this.escapeAttr(image.width || 533)}"
               height="${this.escapeAttr(image.height || 533)}">
        </div>
      </div>
    `;
  }

  renderQuickAdd(product, wrapperId, variantId, maxQuantity, cartQuantity) {
    const inCart = cartQuantity > 0;
    const hasStore = this.hasExplicitStore();
    // Browse mode (Option B): no store-specific price/id to submit - ATC
    // itself stays gated elsewhere (cart.js/theme-functions.js) regardless.
    const locationPriceValue = hasStore ? product.locationPrice.toFixed(2) : '';
    const locationIdValue = hasStore ? product.selectedLocation : '';
    return `
      <div class="quick-add no-js-hidden">
        <product-form
          data-uom="${this.escapeAttr(product.uom?.value || '')}"
          data-conversion-factor="${this.escapeAttr(product.conversion?.value || '')}"
          data-hideinstate="${this.escapeAttr(product.hideInState?.value || '')}"
          data-giftcard=""
          data-deliverypass=""
          data-available-locations="${this.escapeAttr(JSON.stringify(product.availableLocations))}"
          id="product-form-${this.escapeAttr(wrapperId)}">
          <div class="updating-cart-overlay"></div>
          <div class="quick-add__quantity"
               data-id="${this.escapeAttr(variantId)}"
               style="display: ${inCart ? 'flex' : 'none'};">
            <div class="quantity-selectors__collection">
              <div class="collection-product__minus">${this.getMinusIcon()}</div>
              <input class="product-quantity-${this.escapeAttr(this.numericId(product.id))} collection-quantity-selector"
                     value="${inCart ? this.escapeAttr(cartQuantity) : ''}"
                     pattern="[0-9]*"
                     max="${this.escapeAttr(maxQuantity)}"
                     type="number">
              <div class="collection-product__plus">${this.getPlusIcon()}</div>
            </div>
          </div>
          <div class="no-form-wrapper"
               id="${this.escapeAttr(wrapperId)}">
            <input type="hidden"
                   name="id"
                   value="${this.escapeAttr(variantId)}"
                   disabled>
            <input type="hidden"
                   name="properties[_location_price]"
                   value="${this.escapeAttr(locationPriceValue)}"
                   data-location-price>
            <input type="hidden"
                   name="properties[_location_id]"
                   value="${this.escapeAttr(locationIdValue)}"
                   data-location-id>
            <button type="submit"
                    name="add"
                    class="js-go-cart-add-to-cart submit-button quick-add__submit quick-add__submit-search touch-submit button button--full-width button--secondary"
                    ${inCart ? 'style="visibility: hidden; pointer-events: none; opacity: 0;"' : ''}
                    aria-haspopup="dialog">
              <span class="js-go-cart-add-to-cart touch-submit" data-add-to-cart-label="Add to cart">
                Add to cart
              </span>
              <span class="sold-out-message hidden">Sold out</span>
              <div class="loading-overlay__spinner hidden"></div>
            </button>
          </div>
        </product-form>
      </div>
    `;
  }

  renderSafeViewButton(productUrl) {
    return `
      <div class="quick-add no-js-hidden">
        <a href="${this.escapeAttr(productUrl)}"
           class="button button--full-width button--secondary view-button">View</a>
      </div>
    `;
  }

  renderSearchForLink(term, location) {
    const baseUrl = this.buildSearchUrl(term, null);
    const searchUrl = this.buildSearchUrl(term, location);
    return `
      <div id="predictive-search-option-search-keywords"
           class="predictive-search__search-for-button">
        <a href="${this.escapeAttr(searchUrl)}"
           class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow"
           data-search-base-url="${this.escapeAttr(baseUrl)}"
           tabindex="-1"
           role="option"
           aria-selected="false">
          <span data-predictive-search-search-for-text>Search for “${this.escapeHtml(term)}”</span>
          ${this.getArrowIcon()}
        </a>
      </div>
    `;
  }

  renderLoadingState() {
    return `
      <div class="predictive-search__loading-state" aria-hidden="true" style="display: none;">
        <div style="display: flex; flex-direction: column; align-items: center; gap: 1rem; padding: 2rem;">
          <div class="predictive-search__loading-text"
               style="color: #666; font-size: 1.5rem; font-weight: 600; margin-bottom: 0.5rem;">Loading...</div>
          <div class="predictive-search__progress-bar"
               style="width: 200px; height: 4px; background: #e0e0e0; border-radius: 2px; overflow: hidden;">
            <div class="predictive-search__progress-fill"
                 style="height: 100%; background: linear-gradient(90deg, #4CAF50 0%, #8BC34A 100%); width: 0%; transition: width 0.3s ease;"></div>
          </div>
        </div>
      </div>
    `;
  }

  buildSearchUrl(term, location) {
    const url = new URL(
      (typeof routes !== 'undefined' && routes.search_url) || '/search',
      window.location.origin
    );
    url.searchParams.set('q', term);
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('type', 'product,article,collection');
    // 'browse' is the internal sentinel for "no explicit store" - it must
    // never leak into a URL as if it were a real location filter value.
    if (location && location !== 'browse') {
      url.searchParams.set('filter.p.m.custom.available_locations', location);
    }
    return `${url.pathname}${url.search}`;
  }

  getNativeSearchUrl(term) {
    const url = new URL(routes.predictive_search_url, window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('section_id', 'predictive-search');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[type]', 'product,query,article,collection,page');
    url.searchParams.set('resources[options][unavailable_products]', 'show');
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,variants.sku,vendor'
    );
    return url.toString();
  }

  async getNativeFallbackResults(request, cartPromise) {
    try {
      const response = await fetch(this.getNativeSearchUrl(request.term), {
        signal: request.signal,
        credentials: 'same-origin',
      });
      if (!response.ok) {
        throw new Error(`Native predictive search failed (${response.status})`);
      }

      const text = await response.text();
      if (!this.isCurrentRequest(request)) return;
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const section = doc.querySelector('#shopify-section-predictive-search');
      if (!section) throw new Error('Native predictive search section was missing');

      const validCards = this.filterNativeCardsBeforeRender(
        Array.from(section.querySelectorAll('.product-item')),
        request.location
      );
      validCards.slice(10).forEach((card) => card.remove());
      const renderedProductCount = Math.min(validCards.length, 10);
      const queryCount = section.querySelectorAll(
        '#predictive-search-results-queries-list > li'
      ).length;
      const pageCount = section.querySelectorAll(
        '#predictive-search-results-pages-list-desktop > li'
      ).length;
      const liveCount = renderedProductCount + queryCount + pageCount;
      const liveCountElement = section.querySelector(
        '[data-predictive-search-live-region-count-value]'
      );
      if (liveCountElement) {
        liveCountElement.textContent =
          liveCount === 1 ? '1 result' : `${liveCount} results`;
      }

      const cart = await cartPromise;
      if (!this.isCurrentRequest(request)) return;

      this.seedCartState(section, cart);
      this.renderSearchResults(section.innerHTML, true);
    } catch (error) {
      if (error.name === 'AbortError' || !this.isCurrentRequest(request)) return;
      console.error('[PredictiveSearch] Native fallback failed:', error);
      this.clearLoadingTimer();
      this.removeAttribute('loading');
      if (!this.getAttribute('results')) this.close();
    }
  }

  filterNativeCardsBeforeRender(cards, location) {
    const hasStore = location !== 'browse' && this.hasExplicitStore();
    const valid = [];
    for (const card of cards) {
      const variantId = String(card.getAttribute('data-variant-id') || '');
      const frozen = card.getAttribute('data-frozen') === 'true';
      if (!/^\d+$/.test(variantId) || frozen) {
        card.remove();
        continue;
      }

      const rawAvailableLocations = card.getAttribute('data-available-locations');
      const availableLocations = this.parseAvailableLocations(rawAvailableLocations);

      if (!hasStore) {
        // Liquid seeds the maximum excluding location 1. Reuse the shared
        // local renderer for legacy cards and to remove any stale store special.
        const hasAvailableLocationsValue = !!rawAvailableLocations && rawAvailableLocations !== 'null';
        if (hasAvailableLocationsValue && availableLocations.length === 0) {
          card.remove();
          continue;
        }
        window.productCardDataManager?.surfaceBrowseCataloguePrices(card);
        valid.push(card);
        continue;
      }

      let locationPrice = 0;
      try {
        const prices = JSON.parse(card.getAttribute('data-location-prices') || '{}');
        locationPrice = Number.parseFloat(prices[location]);
      } catch (error) {
        locationPrice = 0;
      }

      const hidden = this.isHiddenAtLocation(
        card.getAttribute('data-hideinstate'),
        location
      );
      if (
        !availableLocations.includes(String(location)) ||
        !(locationPrice > 0) ||
        hidden
      ) {
        card.remove();
        continue;
      }

      card.querySelectorAll('.from_price').forEach((element) => {
        element.textContent = `$${locationPrice.toFixed(2)}`;
      });
      card.querySelectorAll('[data-location-price]').forEach((input) => {
        input.value = locationPrice.toFixed(2);
      });
      card.querySelectorAll('[data-location-id]').forEach((input) => {
        input.value = location;
      });
      valid.push(card);
    }
    return valid;
  }

  seedCartState(container, cart) {
    const quantities = this.getCartQuantities(cart);
    container.querySelectorAll('.product-item[data-variant-id]').forEach((card) => {
      const variantId = String(card.getAttribute('data-variant-id') || '');
      const quantity = quantities.get(variantId) || 0;
      const quantityContainer = card.querySelector('.quick-add__quantity');
      const quantityInput = card.querySelector('.collection-quantity-selector');
      const addButton = card.querySelector('.quick-add__submit-search');
      card.classList.toggle('in-cart', quantity > 0);
      if (quantityContainer) quantityContainer.style.display = quantity > 0 ? 'flex' : 'none';
      if (quantityInput) quantityInput.value = quantity > 0 ? quantity : '';
      if (addButton) {
        addButton.style.visibility = quantity > 0 ? 'hidden' : '';
        addButton.style.pointerEvents = quantity > 0 ? 'none' : '';
        addButton.style.opacity = quantity > 0 ? '0' : '';
      }
    });
  }

  renderSearchResults(resultsMarkup, processNativeCards = false) {
    this.clearLoadingTimer();
    if (!this.isModalSearch && this.searchModalIsOpen()) {
      this.close();
      return;
    }

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    if (processNativeCards) {
      const cards = Array.from(
        this.predictiveSearchResults.querySelectorAll('.product-item')
      );
      if (cards.length && window.productCardDataManager) {
        try {
          window.productCardDataManager.processProductCards(cards);
        } catch (error) {
          console.error(
            '[PredictiveSearch] Native card enhancement failed; verified prices remain in place:',
            error
          );
        }
      } else if (cards.length) {
        console.warn(
          '[PredictiveSearch] ProductCardDataManager unavailable; verified native cards remain link-only or disabled.'
        );
      }
    }

    this.setAttribute('results', 'true');
    updatePredictiveSearchLocation();
    if (typeof window.updateDeliveryPassButtons === 'function') {
      window.updateDeliveryPassButtons();
    }
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countElement = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );
    this.setLiveRegionText(countElement?.textContent?.trim() || 'Search results updated');
  }

  setLiveRegionLoadingState(request) {
    this.statusElement =
      this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    // Live-region accessibility feedback is immediate; the visual spinner is
    // deliberately delayed below so fast product responses never flash it.
    this.setLiveRegionText(this.loadingText || 'Loading');

    this.clearLoadingTimer();
    this.loadingTimer = setTimeout(() => {
      this.loadingTimer = null;
      if (!this.isCurrentRequest(request)) return;
      this.showVisualLoadingState();
    }, this.loadingDelayMs);
  }

  showVisualLoadingState() {
    this.setAttribute('loading', 'true');

    const loadingState = this.querySelector('.predictive-search__loading-state');
    if (loadingState) {
      loadingState.setAttribute('aria-hidden', 'false');
      loadingState.style.display = 'flex';
      loadingState.style.justifyContent = 'center';
      loadingState.style.padding = '1rem';
    }
    const loadingTextElement = this.querySelector('.predictive-search__loading-text');
    if (loadingTextElement) loadingTextElement.textContent = 'Loading...';
    const progressFill = this.querySelector('.predictive-search__progress-fill');
    if (progressFill) {
      progressFill.style.width = '0%';
      setTimeout(() => {
        if (this.getAttribute('loading')) progressFill.style.width = '40%';
      }, 100);
      setTimeout(() => {
        if (this.getAttribute('loading')) progressFill.style.width = '70%';
      }, 300);
    }
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => {
      this.statusElement?.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  getResultsMaxHeight() {
    const header = document.querySelector('.section-header');
    this.resultsMaxHeight = header
      ? `${window.innerHeight - header.getBoundingClientRect().bottom}px`
      : `${window.innerHeight}px`;
    return this.resultsMaxHeight;
  }

  open() {
    if (!this.isModalSearch && this.searchModalIsOpen()) return;
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || this.getResultsMaxHeight();
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    this.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', 'false');
    this.input.setAttribute('aria-activedescendant', '');
    this.clearLoadingTimer();
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.resultsMaxHeight = false;
  }

  onModalClassChange() {
    const searchModal = document.querySelector('.search-modal .modal__content');
    if (searchModal?.classList.contains('open_modal')) {
      if (this.closest('.search-modal') && this.getQuery().length) this.open();
      else this.close();
    } else {
      this.close();
    }
  }

  numericId(gid) {
    return String(gid || '').split('/').pop();
  }

  safeImageUrl(value) {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      return url.protocol === 'https:' ? url.href : '';
    } catch (error) {
      return '';
    }
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  escapeAttr(value) {
    return this.escapeHtml(value);
  }

  getMinusIcon() {
    return '<svg width="10" height="2" viewBox="0 0 10 2" fill="none" aria-hidden="true"><path d="M1 1H9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
  }

  getPlusIcon() {
    return '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M1 5H9M5 1V9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
  }

  getCartIcon() {
    return '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M1 1H3.5L4.5 12H13.5L15 4H4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><circle cx="6" cy="14" r="1" stroke="currentColor" stroke-width="1.5"/><circle cx="12" cy="14" r="1" stroke="currentColor" stroke-width="1.5"/></svg>';
  }

  getArrowIcon() {
    return '<svg class="icon icon-arrow" viewBox="0 0 466.205 466.205" aria-hidden="true"><path d="M416.193 170.894 253.049 7.536c-10.94-10.88-28.64-10.83-39.52.11L50.169 170.736c-10.96 10.87-11.03 28.57-.16 39.52 10.87 10.96 28.57 11.03 39.52.16l115.73-115.47v343.31c0 15.44 12.51 27.95 27.95 27.95s27.95-12.51 27.95-27.95V94.946l115.19 115.47c11.07 10.67 28.61 10.67 39.68 0 10.96-10.87 11.03-28.57.16-39.52z"/></svg>';
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}

function getActiveSearchLocation() {
  return typeof window.getExplicitPickingStore === 'function' ? window.getExplicitPickingStore() : null;
}

function hasExplicitSearchStore() {
  return Boolean(getActiveSearchLocation());
}

function appendLocationToSearchLink(link, currentLocation) {
  const href = link.getAttribute('href');
  if (!href || href === '#') return;
  try {
    const url = new URL(href, window.location.origin);
    const searchPath =
      (typeof routes !== 'undefined' && routes.search_url) || '/search';
    if (url.origin !== window.location.origin || url.pathname !== searchPath) return;
    if (hasExplicitSearchStore() && currentLocation) {
      url.searchParams.set(
        'filter.p.m.custom.available_locations',
        currentLocation
      );
    } else {
      url.searchParams.delete('filter.p.m.custom.available_locations');
      url.searchParams.delete('filter.p.m.custom.special_locations');
    }
    link.setAttribute('href', `${url.pathname}${url.search}`);
  } catch (error) {
    // Leave malformed links untouched.
  }
}

function updatePredictiveSearchLocation() {
  const hasStore = hasExplicitSearchStore();
  const currentLocation = getActiveSearchLocation();

  document
    .querySelectorAll('.predictive-search__item--term[data-search-base-url]')
    .forEach((searchLink) => {
      const baseUrl = searchLink.getAttribute('data-search-base-url');
      if (!baseUrl) return;
      try {
        const url = new URL(baseUrl, window.location.origin);
        if (hasStore) {
          url.searchParams.set(
            'filter.p.m.custom.available_locations',
            currentLocation
          );
        } else {
          url.searchParams.delete('filter.p.m.custom.available_locations');
          url.searchParams.delete('filter.p.m.custom.special_locations');
        }
        searchLink.setAttribute('href', `${url.pathname}${url.search}`);
      } catch (error) {
        // Leave malformed links untouched.
      }
    });

  document
    .querySelectorAll('predictive-search a.predictive-search__item[href]')
    .forEach((link) => {
      if (!link.hasAttribute('data-search-base-url')) {
        appendLocationToSearchLink(link, currentLocation);
      }
    });

  document.querySelectorAll('form[role="search"]').forEach((form) => {
    let locationInput = form.querySelector(
      'input[name="filter.p.m.custom.available_locations"]'
    );
    if (!hasStore) {
      // Browse mode: strip any previously-added filter so a plain search stays union.
      form.querySelectorAll('input[name="filter.p.m.custom.available_locations"], input[name="filter.p.m.custom.special_locations"]').forEach(input => input.remove());
      return;
    }
    if (!locationInput) {
      locationInput = document.createElement('input');
      locationInput.type = 'hidden';
      locationInput.name = 'filter.p.m.custom.available_locations';
      form.appendChild(locationInput);
    }
    locationInput.value = currentLocation;
  });
}

function initPredictiveSearchLocation() {
  updatePredictiveSearchLocation();
  if (!window.locationManager) {
    document.addEventListener(
      'locationManagerReady',
      updatePredictiveSearchLocation,
      { once: true }
    );
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPredictiveSearchLocation);
} else {
  initPredictiveSearchLocation();
}
document.addEventListener('locationChanged', updatePredictiveSearchLocation);

function handleSearchSubmit(event) {
  const form = event.target;
  const searchInput = form.querySelector('input[type="search"]');
  if (!searchInput?.value.trim()) return;

  const searchUrl = new URL('/search', window.location.origin);
  searchUrl.searchParams.set('q', searchInput.value.trim());
  searchUrl.searchParams.set('options[prefix]', 'last');
  searchUrl.searchParams.set('type', 'product,article,collection');
  if (hasExplicitSearchStore()) {
    searchUrl.searchParams.set(
      'filter.p.m.custom.available_locations',
      getActiveSearchLocation()
    );
  }
  event.preventDefault();
  event.stopPropagation();
  window.location.href = searchUrl.toString();
}

function interceptHeaderSearchSubmission() {
  document
    .querySelectorAll('predictive-search form[role="search"], search-form form[role="search"]')
    .forEach((form) => {
      form.removeEventListener('submit', handleSearchSubmit, true);
      form.addEventListener('submit', handleSearchSubmit, true);
    });
}

function setupSearchSubmitInterceptors() {
  interceptHeaderSearchSubmission();
  setTimeout(interceptHeaderSearchSubmission, 500);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupSearchSubmitInterceptors);
} else {
  setupSearchSubmitInterceptors();
}

const targetNode = document.querySelector('.search-modal .modal__content');
if (targetNode) {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        mutation.target.dispatchEvent(new Event('classChange'));
      }
    }
  });
  observer.observe(targetNode, { attributes: true, attributeFilter: ['class'] });
}
